import { Box, Dialog, DialogActions, DialogContent, TextField as MuiTextField, FormControl, styled } from "@mui/material";
import { useEffect, useState, useCallback } from "react"; // 添加 useCallback
import {
    List,
    Datagrid,
    TextField,
    TextInput,
    TopToolbar,
    SelectInput,
    FunctionField,
    useDataProvider,
    Button,
    useNotify,
    useListContext,
    DateField,
    useRefresh
} from "react-admin";
import PasswordVerifiers from "../deploy/passwordVerifiers";
import { tokenManager } from "../../Services/authProvider";

interface FilterValues {
    saleCustomerId?: string;
    trafficCustomerIds?: string | string[];
    sns?: string | string[];
}

const ListActions = ({ onFilterChange }: { onFilterChange: (filters: FilterValues) => void }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh(); // 获取刷新函数
    const { setFilters, filterValues, total } = useListContext();
    const initialFilters = { trafficStatusList: [4, 5] };
    const [runDialogOpen, setRunDialogOpen] = useState(false);
    const role = localStorage.getItem("permissions");

    const handleCloseRunDialog = () => {
        setRunDialogOpen(false);
    };
    const handleOpenRunDialog = () => {
        setRunDialogOpen(true);
    };

    // 当过滤值变化时通知父组件
    useEffect(() => {
        onFilterChange({
            saleCustomerId: filterValues.saleCustomerId,
            trafficCustomerIds: filterValues.trafficCustomerIds,
            sns: filterValues.sns
        });
    }, [filterValues, onFilterChange]);

    const handleResetFilters = () => {
        setFilters(initialFilters);
    };

    const hasUserFilters = () => {
        // 排除默认的 trafficStatusList 字段，检查其他字段是否有值
        const userFilterKeys = Object.keys(filterValues).filter(key =>
            key !== 'trafficStatusList' && filterValues[key] != null && filterValues[key] !== ''
        );
        return userFilterKeys.length > 0;
    };

    const exportDetails = () => {
        if (window.confirm(`确认导出流量明细列表？`)) {
            const formData = {
                ...filterValues,
                trafficStatusList: [4, 5] // 添加与查询相同的过滤条件
            };
            dataProvider.deviceInfoExport(formData)
                .then(() => {
                    notify('确认导出流量明细列表成功', { type: 'success' });
                })
                .catch((error: any) => {
                    notify(error.message || '确认导出流量明细列表失败', { type: 'error' });
                })
        }
    }


    const handleStopTraffic = async () => {
        if (total == 0) {
            notify('无可用设备停止流量', { type: 'error' })
            return
        }
        const formData = {
            salesCustomerIds: filterValues.saleCustomerId,
            trafficCustomerIds: filterValues.trafficCustomerIds,
            deviceSns: Array.isArray(filterValues.sns)
                ? filterValues.sns
                : [filterValues.sns].filter(Boolean)
        };
        dataProvider.endRunningVolume(formData)
            .then(() => {
                notify('停止流量成功', { type: 'success' })
                refresh(); // 刷新列表数据
                handleCloseRunDialog()
            })
            .catch((error: any) => notify(error.message || '停止流量失败', { type: 'error' }));
    };

    return (
        <TopToolbar>
            <Button
                label="重置搜索"
                onClick={handleResetFilters}
                disabled={!hasUserFilters()}
            />
            <Button
                label="导出"
                onClick={exportDetails}
            />
            {role != 'ops' ? <Button label="停止流量" onClick={handleOpenRunDialog} /> : null}
            <PasswordVerifiers
                open={runDialogOpen}
                onClose={handleCloseRunDialog}
                onConfirm={handleStopTraffic}
                title="停止流量操作密码"
                confirmLabel="停止流量"
            />
        </TopToolbar>
    );
};

export const equipmentDetails = () => {
    const dataProvider = useDataProvider();
    const [customerTypeChoices, setSaleCustomers] = useState([]);
    const [trafficCustomers, setTrafficCustomers] = useState([]);
    const [strategyCustomers, setStrategyCustomers] = useState([]);
    const [provinceTypeChoices, setProvinceTypeChoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mySn, setMySn] = useState('')
    const notify = useNotify();
    const refresh = useRefresh(); // 获取刷新函数
    const role = localStorage.getItem("permissions");

    const [runDialogOpen, setRunDialogOpen] = useState(false);

    const handleCloseRunDialog = () => {
        setRunDialogOpen(false);
    };
    const handleOpenRunDialog = (sn: any) => {
        setRunDialogOpen(true);
        setMySn(sn)
    };

    // 存储当前过滤值
    const [currentFilters, setCurrentFilters] = useState<FilterValues>({});

    // 过滤值变化时的回调
    const handleFilterChange = useCallback((filters: FilterValues) => {
        setCurrentFilters(filters);
    }, []);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                setLoading(true);
                const [salesData, strategyData, trafficData, provinceData] = await Promise.all([
                    dataProvider.getSalesCustomers(),
                    dataProvider.getStrategyList(),
                    dataProvider.getTrafficCustomers(),
                    dataProvider.getProvinceList()
                ]);

                setSaleCustomers(salesData.map(item => ({ id: item.id, name: item.name })));
                setStrategyCustomers(strategyData.map(item => ({ id: item.id, name: item.name })));
                setTrafficCustomers(trafficData.map(item => ({ id: item.id, name: item.name })));
                setProvinceTypeChoices(provinceData.data.map((province: any) => ({
                    id: province.name,
                    name: province.name
                })));
            } catch (error) {
                console.error("获取客户数据失败:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCustomers();
    }, []);

    const parseSns = (value: any) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') {
            return value.split(/[,，]+/)
                .map(sn => sn.trim())
                .filter(sn => sn.length > 0);
        }
        return [];
    };

    const formatSns = (value: any) => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
    };

    const postFilters = [
        <SelectInput
            alwaysOn
            source="saleCustomerIds"
            label="设备商家"
            choices={customerTypeChoices}
            disabled={loading}
        />,
        <SelectInput
            alwaysOn
            source="trafficCustomerIds" // 注意这里修正为单数形式，与状态一致
            label="流量商家"
            choices={trafficCustomers}
            disabled={loading}
        />,
        <TextInput
            label="设备SN"
            alwaysOn
            source="sns"
            parse={parseSns}
            format={formatSns}
        />,
        <SelectInput
            alwaysOn
            source="province"
            label="设备位置"
            choices={provinceTypeChoices}
        />,
        <SelectInput
            alwaysOn
            source="strategyId" // 注意这里修正为单数形式，与状态一致
            label="流量策略"
            choices={strategyCustomers}
            disabled={loading}
        />
    ];

    const handleSn = async () => {
        const formData = {
            salesCustomerIds: currentFilters.saleCustomerId,
            trafficCustomerIds: currentFilters.trafficCustomerIds,
            deviceSns: [mySn] // 单个设备SN
        };

        await dataProvider.endRunningVolume(formData)
            .then(() => {
                notify('停止流量成功', { type: 'success' })
                handleCloseRunDialog()
                refresh(); // 刷新列表数据
            })
            .catch((error: any) => notify(error.message || '停止流量失败', { type: 'error' }));
    };


    return (
        <List
            filter={{ trafficStatusList: [4, 5] }}
            filters={postFilters}
            actions={<ListActions onFilterChange={handleFilterChange} />}
            empty={false}
            sx={{
                "& .RaList-content": {
                    overflow: "auto",
                }
            }}
        >
            <Datagrid bulkActionButtons={false}>
                <TextField source="id" label="序号" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="sn" label="设备SN" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    label="累计流量"
                    source="totalTraffic"
                    render={record => {
                        const value = Number(record.totalTraffic);
                        if (isNaN(value)) return '0.000 TB';

                        // 将字节转换为TB并保留三位小数
                        const tb = (value / (1024 * 1024 * 1024 * 1024)).toFixed(3);
                        return `${tb} TB`;
                    }}
                    sortable={false}
                />
                <TextField source="trafficCustomerName" label="流量商家" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="saleCustomerName" label="设备商家" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />

                <DateField source="activateTime" label="用户绑定日期" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    label="在线时长"
                    source="totalOnlineDuration"
                    render={record => {
                        const totalSeconds = record.totalOnlineDuration;
                        const days = Math.floor(totalSeconds / 86400);
                        const hours = Math.floor((totalSeconds % 86400) / 3600);

                        if (days > 0) {
                            return `${days}天${hours}小时`;
                        } else {
                            return `${hours}小时`;
                        }
                    }}
                    sortable={false}
                />
                <TextField source="strategyName" label="策略名称" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    label="设备状态"
                    source="deviceStatus"
                    render={record => record.deviceStatus == 1 ? '在线' : '离线'}
                    sortable={false}
                />

                <FunctionField
                    label="设备位置"
                    source="city"
                    render={record =>
                        (record.province || '').replace(/省$/, '') +
                        ' ' +
                        (record.city || '').replace(/市$/, '')
                    }
                    sortable={false}
                />
                <TextField source="operator" label="运营商" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="natType" label="NAT类型" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <FunctionField
                    label="上行网速"
                    source="uploadSpeed"
                    render={record => (record.uploadSpeed || 0) + 'Mbps'}
                    sortable={false}
                    sx={{
                        whiteSpace: 'nowrap',
                    }}
                />
                <TextField source="softwareVersion" label="软件版本" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="firmwareVersion" label="固件版本" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />

                <TextField source="deviceTag" label="带标签" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />

                {role != 'ops' ?
                    <FunctionField
                        label="操作"
                        render={(record) => (
                            <Box sx={{ justifyContent: 'center', display: 'flex', gap: '8px' }}>
                                <Button
                                    disabled={record.deviceStatus != 1}
                                    label="停止流量"
                                    onClick={() => handleOpenRunDialog(record.sn)}
                                />
                            </Box>
                        )}
                    /> : null
                }
            </Datagrid>
            <PasswordVerifiers
                open={runDialogOpen}
                onClose={handleCloseRunDialog}
                onConfirm={handleSn}
                title="停止流量操作密码"
                confirmLabel="停止流量"
            />
        </List>
    );
};